<template>
  <div class="portal-app-page">
    <RetailerHeader />
    <main class="portal-main portal-container checkout-page">
    <PortalPageHeader eyebrow="Secure checkout" title="Complete your order" description="Review your kits, choose a shipping address, and complete payment securely." />

    <div class="checkout-progress" aria-label="Checkout progress">
      <span class="is-complete"><i>1</i> Cart</span><span class="is-active"><i>2</i> Checkout</span><span><i>3</i> Confirmation</span>
    </div>

    <div v-if="loading" class="uk-text-center uk-margin-large">
      <span uk-spinner="ratio: 2"></span>
    </div>

    <div v-else class="checkout-layout">
      <!-- Order Summary -->
      <div class="checkout-flow">
        <div class="uk-card uk-card-default uk-card-body ucari-retailer-card">
          <h3 class="uk-card-title"><span class="checkout-section-number">1</span> Order summary</h3>
          <ul class="uk-list uk-list-divider">
            <li v-for="ci in cart" :key="ci.key" class="uk-flex uk-flex-between">
              <div>
                <div class="uk-text-bold">{{ ci.productName }}</div>
                <p v-if="ci.variationName" class="uk-text-small uk-text-muted uk-margin-remove-top">
                  {{ ci.variationName }}
                </p>
                <div class="uk-text-small">
                  ${{ moneyCents(ci.price) }} × {{ ci.quantity }}
                  <span class="uk-text-muted">
                    ({{ (ci.packSize || packSizeFromVariationName(ci.variationName)) * ci.quantity }} kits)
                  </span>                  
                </div>
              </div>
              <div class="uk-text-bold">
                ${{ moneyCents(ci.price * ci.quantity) }}
              </div>
            </li>
          </ul>
          <hr class="uk-margin-small">
          <div class="uk-flex uk-flex-between">
            <span class="uk-text-bold">Subtotal</span>
            <span class="uk-text-bold">${{ moneyCents(subtotal) }}</span>
          </div>
          <div class="uk-flex uk-flex-between uk-text-large uk-text-bold uk-margin-small-top">
            <span>Total</span>
            <span>${{ moneyCents(subtotal) }}</span>
          </div>
        </div>

        <!-- SHIPPING ADDRESS -->
        <div class="uk-card uk-card-default uk-card-body ucari-retailer-card uk-margin-small-top">
          <h3 class="uk-card-title"><span class="checkout-section-number">2</span> Shipping address</h3>

          <!-- Toggle saved vs new -->
          <div class="uk-margin">
            <label>
              <input class="uk-radio" type="radio" value="saved" v-model="shipMode">
              Use saved address
            </label>
            <label class="uk-margin-left">
              <input class="uk-radio" type="radio" value="new" v-model="shipMode">
              Enter new address
            </label>
          </div>

          <!-- Saved addresses -->
          <div v-if="shipMode === 'saved'">
            <div v-if="!addresses.length" class="uk-alert uk-alert-primary">
              No saved addresses yet.
            </div>
            <ul class="uk-list uk-list-striped" v-else>
              <li v-for="a in addresses" :key="a.id" class="uk-flex uk-flex-between uk-flex-middle">
                <label>
                  <input class="uk-radio" type="radio" :value="a.id" v-model="selectedAddressId">
                  <span class="uk-margin-small-left">
                    {{ a.name }} — {{ a.line1 }}{{ a.line2 ? (', ' + a.line2) : '' }},
                    {{ a.city }}, {{ a.state }} {{ a.postalCode }}
                  </span>
                </label>
              </li>
            </ul>
          </div>

          <!-- New shipping form (all required except line2) -->
          <div v-if="shipMode === 'new'" class="uk-grid-small" uk-grid>
            <div class="uk-width-1-2@s">
              <input
                class="uk-input"
                :class="{ 'uk-form-danger': shippingErrors.name }"
                placeholder="Full name *"
                v-model="newAddress.name"
                @input="clearShippingError('name')"
                maxlength="80"
              >
            </div>
            <!--
            <div class="uk-width-1-2@s">
              <input
                class="uk-input"
                :class="{ 'uk-form-danger': shippingErrors.phone }"
                placeholder="Phone *"
                v-model="newAddress.phone"
                @input="clearShippingError('phone')"
              >
            </div>
            -->

            <div class="uk-width-1-1">
              <input
                class="uk-input"
                :class="{ 'uk-form-danger': shippingErrors.line1 }"
                placeholder="Address line 1 *"
                v-model="newAddress.line1"
                @input="clearShippingError('line1')"
                maxlength="120"
              >
            </div>
            <div class="uk-width-1-1">
              <input
                class="uk-input"
                placeholder="Address line 2"
                v-model="newAddress.line2"
                maxlength="120"
              >
            </div>

            <div class="uk-width-1-2@s">
              <input
                class="uk-input"
                :class="{ 'uk-form-danger': shippingErrors.city }"
                placeholder="City *"
                v-model="newAddress.city"
                maxlength="60"
                @input="clearShippingError('city')"
              >
            </div>
            <div class="uk-width-1-4@s">
              <input
                class="uk-input"
                :class="{ 'uk-form-danger': shippingErrors.state }"
                placeholder="State *"
                v-model="newAddress.state"
                @input="clearShippingError('state')"
                maxlength="30"
              >
            </div>
            <div class="uk-width-1-4@s">
              <input
                class="uk-input"
                :class="{ 'uk-form-danger': shippingErrors.postalCode }"
                placeholder="Zip *"
                v-model="newAddress.postalCode"
                @input="clearShippingError('postalCode')"
                maxlength="15"
              >
            </div>

            <div class="uk-width-1-3@s">
              <select
                class="uk-select"
                :class="{ 'uk-form-danger': shippingErrors.country }"
                v-model="newAddress.country"
                @change="clearShippingError('country')"
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
              </select>
            </div>

            <div class="uk-width-1-1 uk-margin-small">
              <label>
                <input class="uk-checkbox" v-model="saveNewAddress" type="checkbox">
                Save this address
              </label>
            </div>

            <div v-if="addressError" class="uk-alert-danger" uk-alert>
              {{ addressError }}
            </div>
          </div>
        </div>

        <!-- PAYMENT -->
        <div class="uk-card uk-card-default uk-card-body ucari-retailer-card uk-margin-small-top">
          <h3 class="uk-card-title"><span class="checkout-section-number">3</span> Payment</h3>
          <div>
            <div v-if="!squareReady" class="uk-alert-warning" uk-alert>
              <div>Payment not initialized.</div>
              <div v-if="error" class="uk-text-danger uk-margin-small-top">{{ error }}</div>
              <div v-else class="uk-text-muted">Please refresh or go back to cart.</div>
            </div>

            <div v-else>
              <!-- Saved cards -->
              <div v-if="cardsOnFile.length" class="uk-margin">
                <ul class="uk-list uk-list-divider">
                  <li
                    v-for="c in cardsOnFile"
                    :key="c.id"
                    class="uk-flex uk-flex-middle uk-flex-between"
                  >
                    <label>
                      <input
                        class="uk-radio"
                        type="radio"
                        name="paymethod"
                        :value="c.id"
                        v-model="selectedCardId"
                      >
                      <span class="uk-margin-small-left">
                        {{ formatBrand(c.brand) }} •••• {{ c.last4 }}
                        <span v-if="c.expMonth && c.expYear" class="uk-text-meta">
                        (exp {{ c.expMonth }}/{{ c.expYear }})
                        </span>
                      </span>
                    </label>
                    <span
                      uk-icon="icon: trash; ratio: 1.2"
                      class="uk-margin-small-left"
                      style="cursor: pointer;"
                      @click="removeCard(c.id)"
                    ></span>
                  </li>
                </ul>
              </div>

              <!-- New card option -->
              <div class="uk-margin">
                <label>
                  <input
                    class="uk-radio"
                    type="radio"
                    name="paymethod"
                    value="new"
                    v-model="selectedCardId"
                  >
                  <span class="uk-margin-small-left">Use a new card</span>
                </label>
              </div>

              <!-- New Card Element -->
              <div v-if="selectedCardId === 'new'" class="uk-margin">
                <div id="card-container" ref="cardContainer"></div>
                <!-- ensure attach -->
                <span v-show="false">{{ $nextTick(() => attachCardIfNeeded()) }}</span>

                <!-- Billing: same as shipping toggle -->
                <div class="uk-margin-small-top">
                  <label>
                    <input
                      class="uk-checkbox"
                      type="checkbox"
                      v-model="billingSameAsShipping"
                    >
                    <span class="uk-margin-small-left">
                      Billing address is same as shipping address
                    </span>
                  </label>
                </div>

                <!-- Billing form (only if NOT same as shipping) -->
                <div v-if="!billingSameAsShipping" class="uk-grid-small uk-margin-top" uk-grid>
                  <div class="uk-width-1-1">
                    <input
                      class="uk-input"
                      :class="{ 'uk-form-danger': billingErrors.line1 }"
                      placeholder="Billing Address line 1 *"
                      v-model="billingAddress.line1"
                      @input="clearBillingError('line1')"
                      maxlength="120"
                    >
                  </div>
                  <div class="uk-width-1-1">
                    <input
                      class="uk-input"
                      placeholder="Billing Address line 2"
                      v-model="billingAddress.line2"
                      maxlength="120"
                    >
                  </div>
                  <div class="uk-width-1-2@s">
                    <input
                      class="uk-input"
                      :class="{ 'uk-form-danger': billingErrors.city }"
                      placeholder="City *"
                      v-model="billingAddress.city"
                      @input="clearBillingError('city')"
                      maxlength="60"
                    >
                  </div>
                  <div class="uk-width-1-4@s">
                    <input
                      class="uk-input"
                      :class="{ 'uk-form-danger': billingErrors.state }"
                      placeholder="State *"
                      v-model="billingAddress.state"
                      @input="clearBillingError('state')"
                      maxlength="30"
                    >
                  </div>
                  <div class="uk-width-1-4@s">
                    <input
                      class="uk-input"
                      :class="{ 'uk-form-danger': billingErrors.postalCode }"
                      placeholder="Zip *"
                      v-model="billingAddress.postalCode"
                      @input="clearBillingError('postalCode')"
                      maxlength="15"
                    >
                  </div>

                  <div class="uk-width-1-3@s">
                    <select
                      class="uk-select"
                      :class="{ 'uk-form-danger': billingErrors.country }"
                      v-model="billingAddress.country"
                      @change="clearBillingError('country')"
                    >
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                    </select>
                  </div>

                  <div v-if="billingError" class="uk-width-1-1 uk-margin-small-top">
                    <div class="uk-alert-danger" uk-alert>{{ billingError }}</div>
                  </div>
                </div>

                <div class="uk-margin-small-top">
                  <label class="uk-margin-small-top">
                    <input class="uk-checkbox" v-model="saveCardOnFile" type="checkbox">
                    <span class="uk-margin-small-left">
                      Save this card for next time
                    </span>
                  </label>
                </div>
              </div>

              <button
                class="uk-button uk-button-primary"
                :disabled="paying"
                @click="pay"
              >
                <span v-if="paying" uk-spinner></span>
                <span v-else>
                  {{ selectedCardId === 'new'
                    ? 'Pay '
                    : 'Pay ' }}
                  ${{ moneyCents(subtotal) }}
                </span>
              </button>
              <div v-if="error" class="uk-alert-danger uk-margin-small-top" uk-alert>
                {{ error }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </main>
    <RetailerFooter />
  </div>
</template>

<script>
import axios from 'axios';
import RetailerHeader from "./components/RetailerHeader.vue";
import RetailerFooter from './components/RetailerFooter.vue';
import PortalPageHeader from './components/PortalPageHeader.vue';
import { totalCartKits } from '@/utils/cart';

const MAX_CART_KITS = 50;

export default {
  name: 'CheckoutPage',
  components: { RetailerHeader, RetailerFooter, PortalPageHeader },
  data() {
    return {
      accessToken: '',
      cid: '',
      cart: [],
      subtotal: 0, // cents

      // Square
      squareReady: false,
      applicationId: null,
      locationId: null,
      payments: null,
      card: null,
      cardAttached: false,
      cardAttaching: false,

      // Billing address model (full, all required except line2)
      billingAddress: {
        name: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'US'
      },

      // saved cards
      cardsOnFile: [],
      selectedCardId: 'new',
      saveCardOnFile: false,

      // Shipping
      shipMode: 'new',
      addresses: [],
      selectedAddressId: null,
      newAddress: {
        name: '',
        // phone: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'US'
      },
      saveNewAddress: true,
      addressError: null,

      // Validation state
      shippingErrors: {},
      billingErrors: {},
      billingSameAsShipping: true,
      billingError: null,

      loading: true,
      paying: false,
      error: null
    };
  },
  async mounted() {
    document.title = 'Checkout | UCARI';
    this.accessToken = localStorage.getItem('accessToken');
    this.cid = localStorage.getItem('cid');
    this.cart = JSON.parse(localStorage.getItem('ucari_cart') || '[]');
    this.subtotal = this.cart.reduce((s, i) => s + (Number(i.price) * Number(i.quantity)), 0);

    if (!this.cart.length) {
      this.loading = false;
      this.error = 'Your cart is empty.';
      return;
    }

    function getSquareScriptUrlFromAppId(appId) {
      const isSandbox = appId.startsWith('sandbox-');
      return isSandbox
        ? 'https://sandbox.web.squarecdn.com/v1/square.js'
        : 'https://web.squarecdn.com/v1/square.js';
    }

    const ensureSquareSdk = (appId) =>
      new Promise((resolve, reject) => {
        const targetSrc = getSquareScriptUrlFromAppId(appId);

        const existingTag = Array
          .from(document.querySelectorAll('script[src*="web.squarecdn.com"]'))[0];
        const existingSrc = existingTag?.getAttribute('src');

        const hasApi = !!(window.Square && typeof window.Square.payments === 'function');

        if (hasApi && existingSrc === targetSrc) return resolve();

        if (existingTag && existingSrc !== targetSrc) {
          existingTag.parentNode.removeChild(existingTag);
          try {
            delete window.Square;
          } catch (_) {
            // ignore
          }
        }

        const correctTag = document.querySelector(`script[src="${targetSrc}"]`);
        if (correctTag && !hasApi) {
          setTimeout(() =>
            (window.Square && window.Square.payments)
              ? resolve()
              : reject(new Error('Square SDK present but not ready'))
          , 300);
          return;
        }

        if (!correctTag) {
          const s = document.createElement('script');
          s.src = targetSrc;
          s.async = true;
          s.onload = () =>
            (window.Square && window.Square.payments)
              ? resolve()
              : reject(new Error('Square SDK failed to load'));
          s.onerror = () => reject(new Error('Square SDK network error'));
          document.head.appendChild(s);
          return;
        }

        setTimeout(() =>
          (window.Square && window.Square.payments)
            ? resolve()
            : reject(new Error('Square SDK present but not ready'))
        , 300);
      });

    try {
      const { data: cfg } = await axios.post('/api/square/checkout-init', {
        items: this.cart.map(ci => ({
          productId: ci.productId,
          variationId: ci.variationId,
          name: `${ci.productName}${ci.variationName ? ' - ' + ci.variationName : ''}`,
          quantity: ci.quantity,
          price: ci.price,
          currency: ci.currency || 'USD'
        })),
        customerId: localStorage.getItem('squareCustomerId')
      }, {
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          cid: this.cid,
          'x-user-email': localStorage.getItem('userEmail') || ''
        }
      });

      const { success, applicationId, locationId, cards, customerId, customer } = cfg || {};
      console.log('[Square DEBUG] /checkout-init response:', cfg);

      if (!success) throw new Error('checkout-init failed');
      if (!applicationId) throw new Error('Missing applicationId from server');
      if (!locationId) throw new Error('Missing locationId from server');

      this.cardsOnFile = (cards || []).map(x => {
        const expMonthRaw = x.expMonth ?? x.exp_month ?? x.card?.exp_month ?? x.expirationMonth;
        const expYearRaw  = x.expYear  ?? x.exp_year  ?? x.card?.exp_year  ?? x.expirationYear;

        const expMonth = expMonthRaw ? String(expMonthRaw).padStart(2, '0') : '';
        const expYear  = expYearRaw ? String(expYearRaw).slice(-2) : ''; // standard MM/YY display

        return {
          id: x.id,
          brand: x.cardBrand || x.brand || x.card?.card_brand || 'Card',
          last4: x.last4 || x.card?.last_4 || '',
          expMonth,
          expYear
        };
      });
      this.selectedCardId = this.cardsOnFile.length ? this.cardsOnFile[0].id : 'new';

      let serverAddresses = [];
      try {
        const { data: addrRes } = await axios.get('/api/customers/addresses', {
          headers: { Authorization: `Bearer ${this.accessToken}`, cid: this.cid }
        });
        serverAddresses = Array.isArray(addrRes?.addresses) ? addrRes.addresses : [];
      } catch (e) {
        serverAddresses = [];
      }

      const addrFromServer =
        cfg?.customerAddress
        || (customer && customer.address)
        || (
          customer && (customer.address_line_1 || customer.locality || customer.postal_code)
            ? customer
            : null
        );

      this.addresses = [...serverAddresses];

      if (!this.addresses.length && addrFromServer) {
        const synthetic = this.mapSquareAddressToUi(customer, addrFromServer);

        // require basic fields to treat it as a usable shipping address
        const required = ['line1', 'city', 'state', 'postalCode'];
        const hasRequired = required.every(k => synthetic[k] && String(synthetic[k]).trim());

        if (hasRequired) {
          this.addresses.unshift(synthetic);
        } else {
          // no usable Square address → force "new" mode
          this.shipMode = 'new';
          this.selectedAddressId = null;
        }
      }

      if (this.addresses.length) {
        this.shipMode = 'saved';
        this.selectedAddressId = this.addresses[0].id;
      } else {
        this.shipMode = 'new';
        this.selectedAddressId = null;
      }

      await ensureSquareSdk(applicationId);
      axios.defaults.headers.common['x-square-app-id'] = applicationId;
      axios.defaults.headers.common['x-square-env'] =
        applicationId.startsWith('sandbox-') ? 'sandbox' : 'production';

      await this.initSquare(applicationId, locationId, customerId);
      this.squareReady = true;

      await this.$nextTick();
      if (this.selectedCardId === 'new') {
        await this.attachCardIfNeeded();
      }

    } catch (e) {
      console.error('[Square DEBUG] Initialization failed:', e);
      this.error = e?.message || 'Unable to initialize payment';
    } finally {
      this.loading = false;
    }
  },
  methods: {
    moneyCents(cents) { return (Number(cents) / 100).toFixed(2); },

    async initSquare(appId, locId, customerId) {
      if (!appId || !locId) throw new Error('Payment not initialized (missing appId/locationId).');

      this.square = { applicationId: appId, locationId: locId, customerId };
      this.payments = await window.Square.payments(appId, locId);

      if (!this.card) {
        this.card = await this.payments.card();
      }
      console.log('[Square] initSquare OK', this.square);
    },

    async pay() {
      const kits = totalCartKits(this.cart);
      
      if (kits > MAX_CART_KITS) {
        this.error = `Your cart has ${kits} kits. The maximum per order is ${MAX_CART_KITS}. Please adjust your cart.`;
        return;
      }

      if (!this.subtotal) return;
      this.error = null;
      this.addressError = null;
      this.billingError = null;
      this.shippingErrors = {};
      this.billingErrors = {};

      if (!this.square || !this.payments) {
        this.error = 'Payment not initialized.';
        return;
      }

      this.paying = true;
      try {
        // ALWAYS ensure shipping is valid first
        await this.ensureShippingAddress();

        if (this.selectedCardId !== 'new') {
          const { data } = await axios.post('/api/square/pay-with-card-on-file', {
            cardId: this.selectedCardId,
            items: this.cart.map(ci => ({
              ...ci,
              name: `${ci.productName}${ci.variationName ? ' - ' + ci.variationName : ''}`
            })),
            shippingAddress: this.getSelectedAddressPayload(),
            customerId: localStorage.getItem('squareCustomerId')
          }, {
            headers: { Authorization: `Bearer ${this.accessToken}`, cid: this.cid }
          });

          if (!data?.success) throw new Error(data?.error || 'Payment failed');

          localStorage.setItem('ucari_cart', '[]');
          this.$router.push({ name: 'CheckoutSuccess' });
          return;
        }

        // New card flow
        if (!this.card || !this.cardAttached) {
          await this.attachCardIfNeeded();
          if (!this.cardAttached) throw new Error('Card input not ready.');
        }

        const result = await this.card.tokenize();
        if (result.status !== 'OK' || !result.token) {
          throw new Error(result?.errors?.[0]?.message || 'Card tokenization failed');
        }

        const amountDollars = (Number(this.subtotal) / 100).toFixed(2);

        // Build billing source: shipping or separate
        const shippingAddr = this.getSelectedAddressPayload() || {};
        const billingSource = this.billingSameAsShipping
          ? shippingAddr
          : this.billingAddress || {};

        // If not same-as-shipping, validate billing fields
        if (!this.billingSameAsShipping) {
          const msg = this.validateAddress(billingSource, 'billing');
          if (msg) {
            this.billingError = 'Please complete all required billing fields.';
            throw new Error('BILLING_INVALID');
          }
        }

        const b = billingSource || {};
        const name = (b.name || shippingAddr.name || 'UCARI Customer').trim();
        const parts = name.split(/\s+/);

        const billingContact = {
          givenName: parts[0] || 'UCARI',
          familyName: parts.slice(1).join(' ') || 'Customer',
          email: localStorage.getItem('userEmail') || undefined,
          // phone: b.phone || shippingAddr.phone || undefined,
          addressLines: [b.line1, b.line2].filter(Boolean),
          city: b.city || undefined,
          state: b.state || undefined,
          postalCode: b.postalCode || undefined,
          countryCode: (b.country || 'US').toUpperCase()
        };
        Object.keys(billingContact).forEach(k => {
          const v = billingContact[k];
          if (v == null || (Array.isArray(v) && v.length === 0)) delete billingContact[k];
        });

        let verificationToken;
        if (this.saveCardOnFile) {
          if (!billingContact.postalCode) {
            throw new Error('Please enter a billing ZIP/postal code to save this card.');
          }
          const verified = await this.payments.verifyBuyer(result.token, {
            intent: 'STORE',
            billingContact
          });
          verificationToken = verified?.token;
          if (!verificationToken) throw new Error('Buyer verification failed (STORE).');
        } else {
          const verified = await this.payments.verifyBuyer(result.token, {
            intent: 'CHARGE',
            amount: amountDollars,
            currencyCode: 'USD',
            ...(billingContact.postalCode ? { billingContact } : {})
          });
          verificationToken = verified?.token;
          if (!verificationToken) throw new Error('Buyer verification failed (CHARGE).');
        }

        const { data } = await axios.post('/api/square/pay-with-new-card', {
          cardNonce: result.token,
          saveCard: this.saveCardOnFile,
          verificationToken,
          items: this.cart.map(ci => ({
            ...ci,
            name: `${ci.productName}${ci.variationName ? ' - ' + ci.variationName : ''}`
          })),
          billingAddress: billingSource,
          shippingAddress: shippingAddr,
          customerId: localStorage.getItem('squareCustomerId')
        }, {
          headers: { Authorization: `Bearer ${this.accessToken}`, cid: this.cid }
        });

        if (!data?.success) throw new Error(data?.error || 'Payment failed');

        localStorage.setItem('ucari_cart', '[]');
        this.$router.push({ name: 'CheckoutSuccess' });
      } catch (e) {
        console.error(e);

        if (e.message === 'SHIPPING_INVALID') {
          // we already set addressError + red borders
          this.error = ''; // or a generic 'Please fix errors above.'
        } else if (e.message === 'BILLING_INVALID') {
          // we already set billingError + red borders
          this.error = ''; // optional
        } else {
          this.error = e?.message || 'Payment failed';
        }
      } finally {
        this.paying = false;
      }
    },

    async removeCard(cardId) {
      try {
        const res = await fetch(`${process.env.VUE_APP_API_URL}/api/square/remove-card`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.accessToken}`,
            'cid': this.cid
          },
          body: JSON.stringify({ cardId })
        });
        const data = await res.json();
        if (!res.ok || !data?.success) throw new Error(data?.error || 'Could not remove card');
        this.cardsOnFile = this.cardsOnFile.filter(c => c.id !== cardId);
        if (this.selectedCardId === cardId) this.selectedCardId = 'new';
      } catch (e) {
        this.error = e?.message || 'Could not remove card';
      }
    },

    async attachCardIfNeeded() {
      if (this.selectedCardId !== 'new') return;
      if (!this.payments || !this.card) return;
      if (this.cardAttached || this.cardAttaching) return;

      await this.$nextTick();
      const el = document.querySelector('#card-container');
      if (!el) {
        console.warn('[Square] #card-container missing');
        return;
      }

      try {
        this.cardAttaching = true;
        await this.card.attach('#card-container');
        this.cardAttached = true;
        console.log('[Square] card.attach OK');
      } catch (err) {
        if (String(err?.name) === 'PaymentMethodAlreadyAttachedError') {
          this.cardAttached = true;
          console.warn('[Square] already attached (ignored)');
        } else {
          console.error('[Square] attach failed:', err);
          this.error = 'Unable to render card input.';
        }
      } finally {
        this.cardAttaching = false;
      }
    },

    async detachCardIfNeeded() {
      if (!this.card || !this.cardAttached) return;
      try {
        if (typeof this.card.destroy === 'function') await this.card.destroy();
        else if (typeof this.card.unmount === 'function') await this.card.unmount();
        console.log('[Square] card detached');
      } catch (e) {
        console.warn('[Square] detach error (ignored):', e);
      } finally {
        this.cardAttached = false;
        this.cardAttaching = false;
      }
    },

    validateAddress(addr, context = 'shipping') {
      // const shippingRequired = ['name', 'phone', 'line1', 'city', 'state', 'postalCode', 'country'];
      const shippingRequired = ['name', 'line1', 'city', 'state', 'postalCode', 'country'];

      const billingRequired = ['line1', 'city', 'state', 'postalCode', 'country'];

      const required = context === 'shipping' ? shippingRequired : billingRequired;

      const missing = required.filter(k => !String(addr[k] || '').trim());

      const errors = {};
      missing.forEach(k => { errors[k] = true; });

      const limits = {
        name: 80,
        line1: 120,
        line2: 120,
        city: 60,
        state: 30,
        postalCode: 15
      };

      Object.keys(limits).forEach(k => {
        if (addr[k] && String(addr[k]).length > limits[k]) {
          errors[k] = true;
        }
      });
      
      if (context === 'shipping') {
        this.shippingErrors = errors;
      } else if (context === 'billing') {
        this.billingErrors = errors;
      }

      if (missing.length) {
        // optional: log to debug
        console.log('[validateAddress]', context, 'missing:', missing, 'addr:', addr);
        return `Missing: ${missing.join(', ')}`;
      }
      return null;
    },

    getSelectedAddressPayload() {
      if (this.shipMode === 'saved') {
        const addr = this.addresses.find(a => a.id === this.selectedAddressId);
        return addr ? { ...addr } : null;
      }
      return { ...this.newAddress };
    },

    async removeAddress(id) {
      if (id === '__square_primary__') {
        this.addresses = this.addresses.filter(a => a.id !== id);
        if (this.selectedAddressId === id) {
          this.selectedAddressId = this.addresses[0]?.id || null;
          this.shipMode = this.addresses.length ? 'saved' : 'new';
        }
        return;
      }

      try {
        const res = await fetch(`/api/customers/addresses/${encodeURIComponent(id)}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.accessToken}`,
            cid: this.cid
          }
        });
        if (![200, 204, 404].includes(res.status)) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data?.error || `Delete failed (${res.status})`);
        }

        this.addresses = this.addresses.filter(a => a.id !== id);
        if (this.selectedAddressId === id) {
          this.selectedAddressId = this.addresses[0]?.id || null;
          this.shipMode = this.addresses.length ? 'saved' : 'new';
        }
      } catch (e) {
        this.error = e?.message || 'Could not remove address';
      }
    },

    applyPrefilledShipping(pref) {
      if (!this.newAddress) this.newAddress = {};
      Object.assign(this.newAddress, pref);
      this.$forceUpdate?.();
    },

    async ensureShippingAddress() {
      const addr = this.getSelectedAddressPayload();

      if (!addr) {
        this.addressError = 'Please select or enter a shipping address.';
        throw new Error('SHIPPING_INVALID');
      }

      const msg = this.validateAddress(addr, 'shipping');
      if (msg) {
        // field-level flags already set by validateAddress
        this.addressError = 'Please complete all required shipping fields.';
        throw new Error('SHIPPING_INVALID');
      }

      if (this.shipMode === 'new' && this.saveNewAddress) {
        try {
          const { data } = await axios.post('/api/customers/addresses', addr, {
            headers: { Authorization: `Bearer ${this.accessToken}`, cid: this.cid }
          });
          if (data?.id) {
            const saved = { ...addr, id: data.id };
            this.addresses.unshift(saved);
            this.shipMode = 'saved';
            this.selectedAddressId = saved.id;
          }
        } catch (e) {
          // non-fatal
          console.warn('[Checkout] Failed to save shipping address', e);
        }
      }

      this.addressError = null;
      return this.getSelectedAddressPayload();
    },

    mapSquareAddressToUi(customer, addrObj) {
      const name =
        (customer && `${customer.given_name || ''} ${customer.family_name || ''}`.trim()) ||
        localStorage.getItem('userName') ||
        'Shipping';
      return {
        id: '__square_primary__',
        name,
        // phone: (customer && customer.phone_number) || '',
        email: (customer && customer.email_address) || '',
        line1: addrObj.address_line_1 || '',
        line2: addrObj.address_line_2 || '',
        city: addrObj.locality || '',
        state: addrObj.administrative_district_level_1 || '',
        postalCode: addrObj.postal_code || '',
        country: addrObj.country || 'US'
      };
    },

    clearShippingError(field) {
      if (!this.shippingErrors[field]) return;

      const next = { ...this.shippingErrors };
      delete next[field];
      this.shippingErrors = next;

      // Re-check all required shipping fields; if they’re all filled, clear banner
      const addr = this.newAddress || {};
      // const required = ['name', 'phone', 'line1', 'city', 'state', 'postalCode', 'country'];
      const required = ['name', 'line1', 'city', 'state', 'postalCode', 'country'];
      const stillMissing = required.filter(k => !String(addr[k] || '').trim());

      if (!stillMissing.length) {
        this.addressError = null;
      }
    },

    clearBillingError(field) {
      if (!this.billingErrors[field]) return;

      const next = { ...this.billingErrors };
      delete next[field];
      this.billingErrors = next;

      // Only applies when NOT same-as-shipping
      if (this.billingSameAsShipping) return;

      const addr = this.billingAddress || {};
      const required = ['line1', 'city', 'state', 'postalCode', 'country'];
      const stillMissing = required.filter(k => !String(addr[k] || '').trim());

      if (!stillMissing.length) {
        this.billingError = null;
      }
    },

    formatBrand(b) {
      if (!b) return 'Card';
      const map = {
        AMERICAN_EXPRESS: 'Amex',
        DISCOVER: 'Discover',
        MASTERCARD: 'Mastercard',
        VISA: 'Visa',
        JCB: 'JCB',
        UNIONPAY: 'UnionPay'
      };
      return map[b] || String(b).replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    },    
  },
  watch: {
    async selectedCardId(val) {
      if (val === 'new') await this.attachCardIfNeeded();
      else await this.detachCardIfNeeded();
    },
    async squareReady(val) {
      if (val && this.selectedCardId === 'new' && !this.cardAttached) {
        await this.attachCardIfNeeded();
      }
    },
    billingSameAsShipping(newVal) {
      if (newVal) {
        this.billingErrors = {};
        this.billingError = null;
      }
    }
  }
};
</script>
